import dayjs from "dayjs";
import { Check, HelpCircle, X } from "lucide-react";
import type { Variants } from "motion/react";
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useModalStore } from "store/modal/hooks/useModalStore";
import { formatTime, ScrollToElement } from "utils";
import { type BidPostData, getLenderResponseDefaultValues } from "validations";

import type { BadgeProps } from "@krea/shared/design-system/Badge";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import type { FileMetadata } from "@krea/shared/hooks/useFileUpload";
import type { ILenderResponseBenefit } from "@krea/shared/types/lenderResponseBenefit";
import { cn } from "@krea/shared/utils";
import {
  formatLenderMonthlyInterestRate,
  type LenderMonthlyInterestUseCase,
} from "@krea/shared/utils/formatNumber";

import {
  type BidTypeEnum,
  FieldRequestTypeEnum,
} from "@/features/common/enums";
import type { ILenderApplicationExtended } from "@/features/lender-applications/types/lender-application";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import { useSaveBid } from "@/features/lender-responses/hooks/data/use-save-bid.mutation";
import type { IFieldRequest } from "@/features/lender-responses/types/field-request";
import type {
  ICompanyLoanBid,
  ICompanyLoanBidInput,
  IFactoringBid,
  IFactoringBidInput,
  IFieldRequestInput,
  ILeasingBid,
  ILeasingBidInput,
  ILenderResponse,
  IPromotionInput,
  IRealEstateBid,
  IRealEstateBidInput,
  IRevolvingCreditBid,
  IRevolvingCreditBidInput,
  UnionBidInput,
} from "@/features/lender-responses/types/lender-response";

import { useLenderApplicationDetailsStore } from "../lenderApplicationDetailsStore";

export const EmptyField = ({ className }: ComponentProps<"div">) => {
  return (
    <div className={cn(className, "text-gray-600")}>
      <X className="size-4" />
    </div>
  );
};

// Shared by every bid type's form. This single field replaced the separate
// postal and direct-debit fee inputs, so the label has to spell out what now
// belongs in it — otherwise a lender has no way to tell where those went.
export const administrationFeeLabelText = "Administration fees (per month)";

// Rendered next to the input rather than inside the field's <label> —
// FieldLabel wraps its content in a native <label>, and <button> is not a
// valid descendant of <label> (also breaks its content model if the label
// ever gains an associated control).
export const AdministrationFeeTooltip = () => {
  return (
    <ResponsiveTooltip
      trigger={
        <button
          type="button"
          aria-label="About the administration fees"
          className="inline-flex cursor-help items-center justify-center rounded-full text-gray-600 hover:text-gray-500 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <HelpCircle className="size-4" />
        </button>
      }
    >
      Any additional monthly fees you need to charge — for example direct debit,
      postal or other administration fees.
    </ResponsiveTooltip>
  );
};

interface IBidDetailsListItemProps extends ComponentProps<"div"> {
  title: string;
  value: ReactNode;
  /** "summary" = bid summary (table-cell style); "details" = expandable details list */
  variant?: "summary" | "details";
}

export const BidDetailsListItem = ({
  title,
  value,
  className,
  variant = "details",
}: IBidDetailsListItemProps) => {
  if (variant === "details") {
    return (
      <dl className={cn("m-0 my-4 overflow-hidden p-0 last:mb-0", className)}>
        <dt className="text-label">{title}</dt>
        {/* typography: details list value rendered at base size for prominence in expandable bid panels */}
        <dd className="m-0 text-base-strong">{value}</dd>
      </dl>
    );
  }

  return (
    // typography: summary variant uses text-base for table-cell-style bid-summary scannability (intentionally larger than text-base default)
    <div
      className={cn(
        "flex py-1.5 text-base gap-x-4 justify-between items-center w-full my-auto mr-0 xl:mr-2 xl:py-2 xl:w-auto xl:min-w-[150px] xl:flex-col xl:items-start xl:justify-start xl:gap-x-0",
        className,
      )}
    >
      <p className="m-0 shrink-0 text-label">{title}</p>
      <p className="m-0 text-right text-base-strong xl:text-left">{value}</p>
    </div>
  );
};

// Factoring is an invoice purchase, not a loan: there is no loan cost for fees
// to sit inside, the lender pays out an amount.
const FEES_INCLUDED_TEXT = {
  loanCost: "All fees are included in total loan cost",
  paidOutAmount: "All fees are included in total paid out amount",
} as const;

export const BidFeesIncludedNote = ({
  total = "loanCost",
  className,
}: {
  total?: keyof typeof FEES_INCLUDED_TEXT;
  className?: string;
}) => {
  return (
    <div className={cn("mt-4 pb-3 text-base", className)}>
      {FEES_INCLUDED_TEXT[total]}
    </div>
  );
};

export const CheckedField = ({ className }: ComponentProps<"div">) => {
  return (
    <div className={cn(className, "text-green-600")}>
      <Check className="size-4" />
    </div>
  );
};

export const bidFramerVariants: Variants = {
  initial: {
    opacity: 0,
    height: 0,
  },
  visible: (custom) => ({
    opacity: 1,
    height: "auto",
    transition: {
      delay: custom * 0.05,
      type: "tween",
      duration: 0.2,
      ease: "easeOut",
    },
  }),
  hidden: (custom) => ({
    opacity: 0,
    height: 0,
    transition: {
      delay: custom * 0.05,
      type: "tween",
      duration: 0.2,
      ease: "easeOut",
    },
  }),
};

interface IBidPreviewProps {
  preview: true;
  lenderApplication: ILenderApplicationExtended;
  lenderResponse: {
    bids: UnionBidInput[];
    fieldRequests: IFieldRequestInput[];
    // The preview endpoint returns a full LenderResponse, benefits included.
    benefits?: ILenderResponseBenefit[] | null;
    onboardingStartedAt?: undefined;
    creditAssessmentDoneAt?: undefined;
    signingLinkClickedAt?: undefined;
    promotions?: IPromotionInput[];
    // id: number;
  };
}

interface IBidNoPreviewProps {
  preview: false;
  lenderApplication: ILenderApplicationExtended;
  lenderResponse: ILenderResponse;
}

export type IBidProps = IBidPreviewProps | IBidNoPreviewProps;

interface IUserBidCommonPropertiesReturnValue {
  additionalInformationFieldRequests: (IFieldRequestInput | IFieldRequest)[];
  collateralFieldRequests: (IFieldRequestInput | IFieldRequest)[];
  dateCreated: string;
  dateUpdated: string;
  isUpdated: boolean;
  isOpen: boolean;
  readMoreText: string;
  showMore: () => void;
  statusText: string;
  statusVariant: BadgeProps["variant"];
  onDelete: () => void;
  isSelectedLenderResponse: boolean;
}

export const useLenderResponseCommonProperties = ({
  preview,
  lenderResponse,
  lenderApplication,
}: Pick<
  IBidProps,
  "preview" | "lenderResponse" | "lenderApplication"
>): IUserBidCommonPropertiesReturnValue => {
  const { loanApplication } = lenderApplication;
  const showDeleteBidModal = useModalStore((state) => state.showDeleteBidModal);
  const [isOpen, setIsOpen] = useState(false);

  const { fieldRequests } = lenderResponse;
  const { lenderCompanyId } = useParams<{ lenderCompanyId: string }>();

  const isPreview = preview || !("id" in lenderResponse);

  const showMore = () => {
    setIsOpen(!isOpen);
  };
  const readMoreText = isOpen ? "Collapse" : "Show more";

  if (isPreview) {
    return {
      additionalInformationFieldRequests: fieldRequests.filter((fr) => {
        if (fr.type === FieldRequestTypeEnum.ADDITIONAL_INFORMATION) {
          if (("checked" in fr && fr.checked) || !("checked" in fr)) {
            return true;
          }
        }

        return false;
      }),
      collateralFieldRequests: fieldRequests.filter((fr) => {
        if (fr.type === FieldRequestTypeEnum.COLLATERAL) {
          if (("checked" in fr && fr.checked) || !("checked" in fr)) {
            return true;
          }
        }

        return false;
      }),
      dateCreated: "",
      dateUpdated: "",
      isUpdated: false,
      isOpen,
      showMore,
      readMoreText,
      statusText: "",
      statusVariant: undefined,
      onDelete: () => {},
      isSelectedLenderResponse: false,
    } as const;
  }
  const dateCreated = formatTime(lenderResponse.createdAt);
  const dateUpdated = formatTime(lenderResponse.updatedAt);

  const selectedLenderResponseId = loanApplication.selectedLenderResponse?.id;
  const isSelectedLenderResponse =
    !!selectedLenderResponseId &&
    selectedLenderResponseId === lenderResponse.id;

  let statusText = lenderResponse.isActive ? "Active" : "Inactive";
  let statusVariant: BadgeProps["variant"] = lenderResponse.isActive
    ? "success"
    : "destructive";

  if (isSelectedLenderResponse) {
    statusText = "Selected";
    statusVariant = "default";

    if (lenderResponse.paidOutAt) {
      statusText = "Paid out";
      statusVariant = "success";
    }
  }

  // If it's bid preview, only show a field request if it is checked
  // If it is not bid preview, show it if it exists in lender_response
  const collateralFieldRequests = fieldRequests.filter(
    ({ type }) => type === FieldRequestTypeEnum.COLLATERAL,
  );

  const additionalInformationFieldRequests = fieldRequests.filter(
    ({ type }) => type === FieldRequestTypeEnum.ADDITIONAL_INFORMATION,
  );

  const isUpdated = dayjs(lenderResponse.updatedAt).isAfter(
    dayjs(lenderResponse.createdAt),
  );

  const onDelete = () => {
    showDeleteBidModal({
      lenderApplicationId: lenderApplication.id,
      lenderResponseId: lenderResponse.id,
      lenderCompanyId: Number(lenderCompanyId),
    });
  };

  return {
    additionalInformationFieldRequests,
    collateralFieldRequests,
    dateCreated,
    dateUpdated,
    isUpdated,
    isOpen,
    readMoreText,
    showMore,
    statusVariant,
    statusText,
    onDelete,
    isSelectedLenderResponse,
  };
};

export type DynamicLenderResponse =
  | {
      bid: ICompanyLoanBid | ICompanyLoanBidInput;
      bidType: BidTypeEnum.COMPANY_LOAN;
    }
  | {
      bid: IFactoringBid | IFactoringBidInput;
      bidType: BidTypeEnum.FACTORING;
    }
  | {
      bid: IRealEstateBid | IRealEstateBidInput;
      bidType: BidTypeEnum.REAL_ESTATE;
    }
  | {
      bid: ILeasingBid | ILeasingBidInput;
      bidType: BidTypeEnum.LEASING;
    }
  | {
      bid: IRevolvingCreditBid | IRevolvingCreditBidInput;
      bidType: BidTypeEnum.REVOLVING_CREDIT;
    };

interface IUseManageLenderResponseProps {
  bidType: BidTypeEnum;
  lenderApplication: ILenderApplicationExtended;
}

export const useManageLenderResponse = ({
  bidType,
  lenderApplication,
}: IUseManageLenderResponseProps) => {
  const { reset, handleSubmit } = useFormContext<BidPostData>();
  const {
    currentlyUpdatingLenderResponseId,
    setCurrentlyUpdatingLenderResponseId,
  } = useLenderApplicationDetailsStore(
    ({
      currentlyUpdatingLenderResponseId,
      setCurrentlyUpdatingLenderResponseId,
    }) => ({
      currentlyUpdatingLenderResponseId,
      setCurrentlyUpdatingLenderResponseId,
    }),
  );
  const { mutateAsync: placeBid } = useSaveBid();
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const resetLenderResponse = useCallback(() => {
    setCurrentlyUpdatingLenderResponseId(null);
    const initialValues = getLenderResponseDefaultValues(
      bidType,
      lenderApplication.loanApplication,
    );
    reset(initialValues);
  }, [
    bidType,
    reset,
    lenderApplication.loanApplication,
    setCurrentlyUpdatingLenderResponseId,
  ]);

  const handleLenderResponseSubmit = useCallback(() => {
    if (!lenderApplication.id) return;
    handleSubmit(
      async (data) => {
        await placeBid(
          {
            input: data,
            lenderCompanyId: Number(lenderCompany?.id),
            lenderApplicationId: lenderApplication.id,
            lenderResponseId: currentlyUpdatingLenderResponseId
              ? currentlyUpdatingLenderResponseId
              : undefined,
          },
          {
            onSuccess: (response) => {
              const sortedLenderResponses = response.lenderResponses
                ?.filter((it) => it.isActive)
                .sort(
                  (a, b) =>
                    new Date(b.updatedAt).getTime() -
                    new Date(a.updatedAt).getTime(),
                );

              if (sortedLenderResponses && sortedLenderResponses.length > 0) {
                const createdLenderResponse = sortedLenderResponses[0];
                setTimeout(() => {
                  ScrollToElement(
                    `scrollToLenderResponseId-${createdLenderResponse?.id}`,
                  );

                  // Reset the bid form after the scroll
                  // Also, wierd react-hook-form bug where the form is not resetting promotions to empty list correct otherwise
                  // By doing it after the timeout and scroll, it works, but strange that we need to do this.
                  resetLenderResponse();
                }, 200);

                const totalInterestRate = createdLenderResponse?.bids?.reduce(
                  (acc, bid) => {
                    return acc + bid.comparableMonthlyInterestRate;
                  },
                  0,
                );

                let avgInterestRate: number | null = null;

                if (
                  createdLenderResponse?.bids?.length &&
                  createdLenderResponse.bids.length > 0 &&
                  totalInterestRate !== undefined
                ) {
                  avgInterestRate =
                    totalInterestRate / createdLenderResponse.bids.length;
                }

                if (avgInterestRate && avgInterestRate > 10.0) {
                  toast.warning(
                    "Comparable interest seems high - please verify input",
                  );
                }
              }
            },
          },
        );
      },
      (e) => console.error(e),
    )();
  }, [
    currentlyUpdatingLenderResponseId,
    handleSubmit,
    lenderApplication.id,
    lenderCompany?.id,
    placeBid,
    resetLenderResponse,
  ]);

  return {
    resetLenderResponse,
    handleLenderResponseSubmit,
  } as const;
};

export const formatLenderResponseFormData = (
  data: BidPostData,
  useCase: LenderMonthlyInterestUseCase,
  isFeatureInterestRatePerYearActivated?: boolean, // If a lender company has the feature activated, we need to convert the "monthly interest rate" data point.
): BidPostData => {
  // Convert interest rate per year to interest rate per 30 days if the feature is activated

  if (isFeatureInterestRatePerYearActivated) {
    data.bids = data.bids.map((bid) => {
      if ("lenderMonthlyInterest" in bid && bid.lenderMonthlyInterest) {
        return {
          ...bid,
          lenderMonthlyInterest: formatLenderMonthlyInterestRate(
            bid.lenderMonthlyInterest,
            useCase,
            isFeatureInterestRatePerYearActivated,
          ),
        };
      }

      return bid;
    });
  }

  return data;
};

export const getFormValuesFromLenderResponse = (
  lenderResponse: ILenderResponse,
  isFeatureInterestRatePerYearActivated?: boolean,
): BidPostData => {
  const { bids, promotions, fieldRequests, creditTermsFile } = lenderResponse;

  let existingCreditTermsFile: FileMetadata | null = null;

  if (creditTermsFile) {
    existingCreditTermsFile = {
      id: creditTermsFile.id.toString(),
      name: creditTermsFile.originalFileName ?? "",
      size: 0,
      type: "",
      url: creditTermsFile.fileUrl ?? "",
    };
  }

  const bidsForForm = bids.map((bid) => ({
    ...bid,
    formKey: crypto.randomUUID(),
  }));

  return formatLenderResponseFormData(
    {
      bids: bidsForForm,
      fieldRequests,
      promotions,
      creditTermsFile: existingCreditTermsFile,
    },
    "TO_YEARLY",
    isFeatureInterestRatePerYearActivated,
  );
};
